import React, { useState, useEffect } from 'react';
import { Clock, CheckCircle, XCircle, X, Loader2, RefreshCw, AlertCircle } from 'lucide-react';
import {
  getMyChangeRequests,
  cancelChangeRequest,
  type ChangeRequest,
  FIELD_LABELS,
  type ChangeableField,
} from '@/services/changeRequestService';

interface ChangeRequestsSectionProps {
  className?: string;
  onRefresh?: () => void;
}

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  switch (status) {
    case 'PENDING':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">
          <Clock className="w-3 h-3" />
          En attente
        </span>
      );
    case 'APPROVED':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
          <CheckCircle className="w-3 h-3" />
          Approuvée
        </span>
      );
    case 'REJECTED':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
          <XCircle className="w-3 h-3" />
          Refusée
        </span>
      );
    default:
      return null;
  }
};

export const ChangeRequestsSection: React.FC<ChangeRequestsSectionProps> = ({
  className = '',
  onRefresh,
}) => {
  const [requests, setRequests] = useState<ChangeRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadRequests = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getMyChangeRequests();
      setRequests(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement des demandes');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadRequests();
  }, []);

  const handleCancel = async (requestId: string) => {
    setCancellingId(requestId);
    try {
      await cancelChangeRequest(requestId);
      setRequests(prev => prev.filter(r => r.id !== requestId));
      onRefresh?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de l\'annulation');
    } finally {
      setCancellingId(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getFieldLabel = (fieldName: string): string => {
    return FIELD_LABELS[fieldName as ChangeableField] || fieldName;
  };

  if (isLoading) {
    return (
      <div className={`bg-white rounded-xl border border-gray-200 p-6 ${className}`}>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 text-primary-600 animate-spin" />
        </div>
      </div>
    );
  }

  if (requests.length === 0) {
    return null; // Don't show section if no requests
  }

  const pendingRequests = requests.filter(r => r.status === 'PENDING');
  const processedRequests = requests.filter(r => r.status !== 'PENDING');

  return (
    <div className={`bg-white rounded-xl border border-gray-200 p-6 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">
          Mes demandes de modification
        </h2>
        <button
          onClick={loadRequests}
          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          title="Actualiser"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Pending requests */}
      {pendingRequests.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-medium text-gray-700 mb-3">
            En attente ({pendingRequests.length})
          </h3>
          <div className="space-y-3">
            {pendingRequests.map((request) => (
              <div
                key={request.id}
                className="p-4 bg-orange-50 border border-orange-200 rounded-lg"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-medium text-gray-900">
                        {getFieldLabel(request.fieldName)}
                      </span>
                      <StatusBadge status={request.status} />
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Valeur actuelle:</span>
                        <p className="text-gray-700 truncate">
                          {request.currentValue || '(non renseigné)'}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-500">Nouvelle valeur:</span>
                        <p className="text-gray-900 font-medium truncate">
                          {request.requestedValue}
                        </p>
                      </div>
                    </div>
                    {request.reason && (
                      <p className="mt-2 text-sm text-gray-600">
                        <span className="font-medium">Raison:</span> {request.reason}
                      </p>
                    )}
                    <p className="mt-2 text-xs text-gray-500">
                      Soumise le {formatDate(request.createdAt)}
                    </p>
                  </div>
                  <button
                    onClick={() => handleCancel(request.id)}
                    disabled={cancellingId === request.id}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                    title="Annuler la demande"
                  >
                    {cancellingId === request.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <X className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Processed requests */}
      {processedRequests.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-3">
            Historique ({processedRequests.length})
          </h3>
          <div className="space-y-3">
            {processedRequests.slice(0, 5).map((request) => (
              <div
                key={request.id}
                className={`p-4 rounded-lg border ${
                  request.status === 'APPROVED'
                    ? 'bg-green-50 border-green-200'
                    : 'bg-red-50 border-red-200'
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-medium text-gray-900">
                        {getFieldLabel(request.fieldName)}
                      </span>
                      <StatusBadge status={request.status} />
                    </div>
                    <div className="text-sm">
                      <span className="text-gray-500">Demande:</span>{' '}
                      <span className="text-gray-700">{request.currentValue || '(vide)'}</span>
                      <span className="text-gray-400 mx-2">→</span>
                      <span className="text-gray-900 font-medium">{request.requestedValue}</span>
                    </div>
                    {request.reviewComment && (
                      <p className="mt-2 text-sm text-gray-600">
                        <span className="font-medium">Commentaire:</span> {request.reviewComment}
                      </p>
                    )}
                    <p className="mt-2 text-xs text-gray-500">
                      Traitée le {request.reviewedAt ? formatDate(request.reviewedAt) : '—'}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ChangeRequestsSection;
